import { useTranslation } from 'react-i18next';
import { Camera } from 'lucide-react';
import { Button } from '../ui/button';
import { ClimbPhotoSource } from './climb-editor/ClimbPhotoSource';
import { useAddClimbPhoto } from './use-add-climb-photo';
import type { Climb } from '../../types/climb';

/**
 * What stands where a climb's photograph would be, when it has none.
 *
 * The very screen the editor opens with — the camera and the gallery, as big as
 * the decision — because the reader meeting the empty frame is the person
 * standing in front of the wall, and this is the moment the picture exists to be
 * taken. Anybody may: see `wantsPhoto` and the server's own rule.
 *
 * What they pick goes to the editor, where the holds are circled — see
 * `useAddClimbPhoto`, which the camera on a list row shares.
 */
export function ClimbPhotoPrompt({ climb }: { climb: Climb }) {
  const { t } = useTranslation();
  const addPhoto = useAddClimbPhoto(climb);

  // Signed out there is nothing to offer yet: the upload carries a token. So the
  // camera is not opened on a picture that would be dropped at the door — they
  // are sent to sign in, and land on the editor they were headed for.
  if (!addPhoto.signedIn) {
    return (
      <div className="space-y-4 px-4 py-10 text-center" data-testid="climb-photo-prompt">
        <p className="text-sm text-muted-foreground">{t('climbEditor.photo.intro')}</p>
        <Button className="gap-2" onClick={addPhoto.signIn}>
          <Camera className="size-4" aria-hidden />
          {t('climbs.addPhoto')}
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="climb-photo-prompt">
      <ClimbPhotoSource
        // The two shortcuts the editor can offer are the place's, and this is not
        // the place's page: a sector's photo is picked where a route is drawn on
        // it, and the gym's recent walls are its staff's shortcut.
        zones={[]}
        recent={[]}
        onZonePhoto={() => {}}
        onRecentPhoto={() => {}}
        onFile={addPhoto.take}
      />
    </div>
  );
}
